import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import {
  Icon,
  PageHeader,
  PageShell,
  Pagination,
  PlainButton,
  SegmentedFilter,
  SkeletonBar,
  inkA,
  orange,
  tokens,
  useIsMobile,
} from 'components/v3';
import useAdminRouteContext from 'hooks/useAdminRouteContext';
import { fieldErrorsOf } from 'shared/apolloFieldErrors';
import AccountFilter from './components/AccountFilter';
import FormCard from './components/FormCard';
import NewFormModal from './components/NewFormModal';
import NotesDrawer from './components/NotesDrawer';
import {
  useGetFormsAdmin,
  useGetFormsSummary,
  useRestoreForm,
  useSetFormStatusCard,
} from './gql';
import { FormsHelp, HelpButton } from './help';
import { brand } from './shared/brand';
import {
  formBuilderPath,
  formsListPath,
  formSubmissionsPath,
} from './shared/routes';
import { listTourNavigate } from './shared/tourNavigation';
import useFormsToaster from './shared/useFormsToaster';

// Settings → Forms: every builder form as a card grid, filtered by account
// (?account=), view (?view=archived) and page (?page=). The query string is
// the state, so the builder's breadcrumb can link to a filtered list.

export const PAGE_SIZE = 24;

const plural = (n, one, many) =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

export const readListParams = (search) => {
  const q = new URLSearchParams(search || '');
  const account = parseInt(q.get('account'), 10);
  const page = parseInt(q.get('page'), 10);
  return {
    account: account > 0 ? account : null,
    view: q.get('view') === 'archived' ? 'archived' : 'active',
    page: page > 1 ? page : 1,
  };
};

const NewFormTile = styled(PlainButton)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 180px;
  padding: 20px;
  box-sizing: border-box;
  border-radius: 16px;
  border: 2px dashed ${(p) => p.$border};
  color: ${(p) => p.$color};
  text-align: center;
  transition: border-color 150ms ease, color 150ms ease, background 150ms ease;

  &:hover {
    border-color: ${orange(1)};
    color: ${orange(1)};
    background: ${orange(0.05)};
  }
`;

const EmptyCard = ({ title, body }) => {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <div
      style={{
        gridColumn: '1 / -1',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        padding: '40px 20px',
        textAlign: 'center',
        borderRadius: 16,
        background: t.card,
        border: '1px solid ' + t.border,
        color: t.sub,
        fontSize: 14,
      }}
    >
      <Icon name="archive" size={28} color={inkA(theme, 0.3)} />
      <div style={{ fontSize: 16, fontWeight: 700, color: t.text }}>
        {title}
      </div>
      <div style={{ maxWidth: 360, lineHeight: 1.5 }}>{body}</div>
    </div>
  );
};

EmptyCard.propTypes = {
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
};

const CardSkeleton = () => {
  const t = tokens(useTheme());
  return (
    <div
      aria-hidden="true"
      style={{
        display: 'grid',
        gap: 12,
        padding: 20,
        minHeight: 180,
        boxSizing: 'border-box',
        borderRadius: 16,
        background: t.card,
        border: '1px solid ' + t.border,
      }}
    >
      <SkeletonBar $w="40%" $h="11px" />
      <SkeletonBar $w="75%" $h="18px" />
      <SkeletonBar $w="60%" $h="12px" />
      <SkeletonBar $w="100%" $h="34px" $r="9px" />
    </div>
  );
};

const subtitleOf = ({ count, view, account, accountCount, loading }) => {
  if (loading) return '';
  if (!count) return view === 'archived' ? 'No archived forms' : 'No forms yet';
  const forms = plural(count, 'form', 'forms');
  if (account) return forms;
  return `${forms} across ${plural(accountCount, 'account', 'accounts')}`;
};

// The header's account list, the picked account's name, the title and the
// view's total for the filter's "All accounts (N)".
export const listHeading = ({ summary, forms, account, archived }) => {
  const accounts = (summary && summary.accounts) || [];
  const selected = account
    ? accounts.find((a) => a.account_id === account)
    : null;
  const accountName =
    (selected && selected.name) ||
    (account && forms[0] && forms[0].account && forms[0].account.name) ||
    null;
  let viewTotal = 0;
  if (summary)
    viewTotal = summary[archived ? 'archived_count' : 'active_count'];
  return {
    accounts,
    accountName,
    title: account ? accountName || 'Forms' : 'All Forms',
    viewTotal,
  };
};

// Title card with the account filter, Help & tours and New Form. Phones
// (design: builder-mobile.css .forms-head-actions) move the count, the
// filter, Help and a square New Form button to one row under the card.
const ListHeader = ({ title, subtitle, filter, onNew, isMobile }) => {
  const t = tokens(useTheme());
  if (!isMobile) {
    return (
      <PageHeader
        icon="clipboard"
        title={title}
        subtitle={subtitle || '\u00a0'}
      >
        {filter}
        <HelpButton />
        <ButtonV2
          primary
          data-tour="forms-new"
          onClick={onNew}
          icon={<Icon name="plus" size={16} color="currentColor" />}
          style={{ width: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          New Form
        </ButtonV2>
      </PageHeader>
    );
  }
  return (
    <>
      <PageHeader icon="clipboard" title={title} />
      <div style={{ display: 'grid', gap: 10, marginBottom: 14 }}>
        {subtitle ? (
          <div style={{ fontSize: 13, fontWeight: 600, color: t.sub }}>
            {subtitle}
          </div>
        ) : null}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>{filter}</div>
          <HelpButton compact size={44} muted />
          <ButtonV2
            primary
            data-tour="forms-new"
            aria-label="New Form"
            onClick={onNew}
            icon={<Icon name="plus" size={20} color="currentColor" />}
            style={{ width: 44, height: 44, padding: 0, flexShrink: 0 }}
          />
        </div>
      </div>
    </>
  );
};

ListHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  filter: PropTypes.node.isRequired,
  onNew: PropTypes.func.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

ListHeader.defaultProps = { subtitle: '' };

// The grid's tiles: New form (active view, page 1), then skeletons, the
// archived empty card, or the form cards. A load error replaces them all.
const FormTiles = ({
  error,
  forms,
  archived,
  showNewTile,
  firstLoad,
  accountName,
  onNew,
  cardProps,
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  if (error && !forms.length) {
    return (
      <EmptyCard
        title="Couldn't load forms"
        body={fieldErrorsOf(error).global}
      />
    );
  }
  let rest;
  if (firstLoad) {
    rest = [0, 1, 2].map((i) => <CardSkeleton key={`sk${i}`} />);
  } else if (archived && !forms.length) {
    rest = (
      <EmptyCard
        title="Nothing archived yet"
        body="Forms you archive show up here. Restore one to edit it again."
      />
    );
  } else {
    rest = forms.map((form) => (
      <FormCard
        key={form.id}
        form={form}
        archived={archived}
        {...cardProps(form)}
      />
    ));
  }
  return (
    <>
      {showNewTile ? (
        <NewFormTile
          data-help="new-form-card"
          onClick={onNew}
          $border={inkA(theme, 0.18)}
          $color={t.sub}
        >
          <Icon name="plus" size={30} color="currentColor" />
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            Start a new form
          </span>
          <span style={{ fontSize: 12, color: t.faint }}>
            {accountName ? `For ${accountName}` : 'Pick an account, then build'}
          </span>
        </NewFormTile>
      ) : null}
      {rest}
    </>
  );
};

FormTiles.propTypes = {
  error: PropTypes.object,
  forms: PropTypes.arrayOf(PropTypes.object).isRequired,
  archived: PropTypes.bool.isRequired,
  showNewTile: PropTypes.bool.isRequired,
  firstLoad: PropTypes.bool.isRequired,
  accountName: PropTypes.string,
  onNew: PropTypes.func.isRequired,
  cardProps: PropTypes.func.isRequired,
};

FormTiles.defaultProps = { error: null, accountName: null };

const FormsList = () => {
  const history = useHistory();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { routePrefix } = useAdminRouteContext();
  const { showToast, toasterNode } = useFormsToaster();
  const { account, view, page } = readListParams(location.search);
  const archived = view === 'archived';

  const { summary } = useGetFormsSummary(view);
  const { forms, count, loading, error } = useGetFormsAdmin({
    view,
    account_id: account,
    page,
    limit: PAGE_SIZE,
  });
  const setStatus = useSetFormStatusCard();
  const restoreForm = useRestoreForm();

  const [creating, setCreating] = useState(false);
  const [notesFormId, setNotesFormId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const { accounts, accountName, title, viewTotal } = listHeading({
    summary,
    forms,
    account,
    archived,
  });
  const firstLoad = loading && !forms.length;
  const pageCount = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const notesForm = notesFormId
    ? forms.find((f) => f.id === notesFormId)
    : null;

  const go = (next) =>
    history.push(
      formsListPath(routePrefix, {
        account: next.account !== undefined ? next.account : account,
        view: (next.view || view) === 'archived' ? 'archived' : undefined,
        page: next.page > 1 ? next.page : undefined,
      })
    );

  const openBuilder = (form) =>
    history.push(formBuilderPath(routePrefix, form.id));
  const openSubmissions = (form) =>
    history.push(formSubmissionsPath(routePrefix, form.id));

  const toggle = async (form, next) => {
    setBusyId(form.id);
    const { error: failed } = await setStatus(form, next);
    setBusyId(null);
    if (failed) {
      showToast({ type: 'error', title: failed.global });
      return;
    }
    showToast(
      next === 'live'
        ? `Form is now Live in the ${brand().name} app`
        : 'Form switched to Draft — hidden in the app'
    );
  };

  const restore = async (form) => {
    setBusyId(form.id);
    const { error: failed } = await restoreForm(form.id);
    setBusyId(null);
    if (failed) {
      showToast({ type: 'error', title: failed.global });
      return;
    }
    showToast(`“${form.name}” restored`);
  };

  const filter = (
    <AccountFilter
      accounts={accounts}
      value={account}
      total={viewTotal}
      width={isMobile ? '100%' : '260px'}
      onChange={(id) => go({ account: id, page: 1 })}
    />
  );

  const subtitle = subtitleOf({
    count,
    view,
    account,
    accountCount: accounts.length,
    loading: firstLoad,
  });

  const openNew = () => setCreating(true);
  // The product tour's screen requests (help/FormsHelp).
  const tourNavigate = listTourNavigate({
    forms,
    archived,
    account,
    routePrefix,
    push: (path) => history.push(path),
    closeOverlays: () => {
      setCreating(false);
      setNotesFormId(null);
    },
  });
  const cardProps = (form) => ({
    compact: isMobile,
    busy: busyId === form.id,
    onOpen: openBuilder,
    onOpenSubmissions: openSubmissions,
    onToggle: toggle,
    onOpenNotes: (f) => setNotesFormId(f.id),
    onRestore: restore,
  });

  return (
    <>
      <ListHeader
        title={title}
        subtitle={subtitle}
        filter={filter}
        onNew={openNew}
        isMobile={isMobile}
      />

      <div style={{ marginBottom: isMobile ? 14 : 18 }}>
        <SegmentedFilter
          value={view}
          onChange={(key) => go({ view: key, page: 1 })}
          options={[
            {
              key: 'active',
              label: 'All Forms',
              count: summary ? summary.active_count : null,
            },
            {
              key: 'archived',
              label: 'Archived',
              count: summary ? summary.archived_count : null,
            },
          ]}
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile
            ? 'minmax(0, 1fr)'
            : 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))',
          gap: isMobile ? 12 : 16,
        }}
      >
        <FormTiles
          error={error}
          forms={forms}
          archived={archived}
          showNewTile={!archived && page === 1}
          firstLoad={firstLoad}
          accountName={accountName}
          onNew={openNew}
          cardProps={cardProps}
        />
      </div>

      {pageCount > 1 ? (
        <div style={{ marginTop: 20 }}>
          <Pagination
            page={page}
            pageCount={pageCount}
            onChange={(p) => go({ page: p })}
          />
        </div>
      ) : null}

      <NewFormModal
        open={creating}
        onClose={() => setCreating(false)}
        defaultAccountId={account}
      />
      <NotesDrawer
        form={notesForm}
        open={!!notesForm}
        onClose={() => setNotesFormId(null)}
      />
      {toasterNode}
      <FormsHelp page="list" navigate={tourNavigate} />
    </>
  );
};

const FormsPage = () => (
  <PageShell>
    <FormsList />
  </PageShell>
);

export default FormsPage;
